import React from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout/Layout';
import { SearchPage } from '../components/Views/SearchPage';
import { fromUrlToObj } from '../utils/helpers';
import { getBreadcrumbs } from '../utils/helpers/getBreadcrumbs';
import { extractApolloState } from '../utils/helpers/extractApolloState';
import { searchTotalCountQuery } from '../api/queries';
import { withBugsnagGetServerSideProps } from '../utils/withBugsnag';
import { fetchSearchDataOnSSR } from '../utils/helpers/search';
import { formatSearchResultSeoData } from '../utils/helpers/seo';
import { prepareSSRContext } from '../utils/helpers/ssrContext';
import { buildRedirectPath } from '../utils/helpers/slugFilters';

const Search = ({ transformedObj, totalAdsCount, h1, ...rest }) => (
  <SearchPage
    transformedObj={transformedObj}
    totalAdsCount={totalAdsCount}
    h1={h1}
    {...rest}
  />
);

export const getServerSideProps = withBugsnagGetServerSideProps(async ctx => {
  const { req, query, res } = ctx;

  if (res && Array.isArray(query.search)) {
    const redirectUrl = buildRedirectPath(query, query.search);
    if (redirectUrl) {
      res.writeHead(301, { Location: redirectUrl });
      res.end();
      return {};
    }
  }

  const {
    apolloClient,
    apolloOptions,
    currentLang,
    isSSRWebView,
    abTests,
    hasAccessToken
  } = prepareSSRContext(ctx);

  const [dataForTransform, seoDataRaw] = await fetchSearchDataOnSSR(
    currentLang,
    apolloClient,
    req
  );

  const transformedObj = fromUrlToObj(dataForTransform, query);
  const locationIds = transformedObj.filter?.locationIds || [];
  const chosenLocationNames = (dataForTransform.data?.locations || [])
    .filter(location => locationIds.includes(location.id))
    .map(location => location.name.trim());

  const { breadcrumbs } = getBreadcrumbs(
    dataForTransform?.data,
    transformedObj?.filter || {},
    'search-result'
  );

  if (transformedObj.noMatch) {
    res.statusCode = 404;

    return { props: { statusCode: 404 } };
  }

  // eslint-disable-next-line camelcase
  const { category_touched, ...filtersWithoutCategoryTouched } =
    transformedObj.filter;
  transformedObj.filter = filtersWithoutCategoryTouched;

  const totalAdsCountData = await apolloClient.query({
    query: searchTotalCountQuery,
    variables: { filter: filtersWithoutCategoryTouched }
  });

  const totalAdsCount =
    totalAdsCountData?.data?.itemsConnection?.totalCount || 0;

  const seoData = formatSearchResultSeoData(
    seoDataRaw,
    currentLang,
    dataForTransform.data,
    transformedObj?.filter
  );

  return extractApolloState(apolloClient, {
    props: {
      transformedObj,
      seoData,
      totalAdsCount,
      h1: seoData.h1,
      isSearchPage: true,
      breadcrumbs,
      currentLang,
      apolloOptions,
      isSSRWebView,
      abTests,
      hasAccessToken,
      // eslint-disable-next-line camelcase
      isCategoryTouched: !!category_touched,
      chosenLocationNames
    }
  });
}, '/search');

Search.propTypes = {
  transformedObj: PropTypes.object,
  seoData: PropTypes.object,
  breadcrumbs: PropTypes.array,
  totalAdsCount: PropTypes.number,
  h1: PropTypes.string,
  isSearchPage: PropTypes.bool,
  currentLang: PropTypes.string
};

Search.getLayout = (page, pageProps) => (
  <Layout isBackgroundBanner isBanner pageProps={pageProps}>
    {page}
  </Layout>
);

export default Search;
